'use client';

import {
  type ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { getTextFromCookiebot } from '@/lib/cookiebot';
import { CookiebotContext } from '@/lib/cookiebot/cookiebot-context';
import { CookieBanner } from './cookie-banner.client';
import { CookiebotHandler } from './cookiebot-handler.client';
import { type CookieBannerText } from './types';

type Props = {
  data: CookieBannerText | null;
};

export const CookieBannerSwitcher = (props: Props): ReactElement | null => {
  const { data } = props;

  const { isEnabled } = useFeatureToggle();
  const isCookieBannerEnabled = isEnabled('cookie-banner');

  const cookiebot = useContext(CookiebotContext);

  const [cookieBannerText, setCookieBannerText] =
    useState<CookieBannerText | null>(data);

  const updateTextFromCookiebot = useCallback(async (): Promise<void> => {
    if (cookiebot?.dialog) {
      const newCookieBannerText = await getTextFromCookiebot(cookiebot.dialog);
      setCookieBannerText(newCookieBannerText);
    }
  }, [cookiebot?.dialog, setCookieBannerText]);

  // Fallback to Cookiebot's text if we couldn't get it from Dato
  useEffect(() => {
    if (isCookieBannerEnabled && !cookieBannerText) {
      void updateTextFromCookiebot();
    }
  }, [cookieBannerText, isCookieBannerEnabled, updateTextFromCookiebot]);

  return isCookieBannerEnabled ? (
    <CookieBanner cookiebot={cookiebot} text={cookieBannerText} />
  ) : (
    <CookiebotHandler cookiebot={cookiebot} />
  );
};
